"use client";

import { ReactNode } from "react";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";

import { ALL_FEATURE_FLAGS, SPLIT_FLAGS } from "../constants/splitFeatureFlags";

/**
 * Mounts the Split (Harness FME) browser SDK, but only when `SPLIT_FLAGS` has at
 * least one entry — so a flag exists if and only if the SDK is running.
 *
 * ⚠️ Builds no SDK and renders only `children` when the registry is empty. That
 * is intended, not dead code. See `src/constants/splitFeatureFlags.ts`.
 */
export function SplitProvider({
  children,
  userId,
}: {
  children: ReactNode;
  /** CustomUser id. Keep this consistent with any server-side split key. */
  userId?: string | null;
}) {
  if (ALL_FEATURE_FLAGS.length === 0) {
    return <>{children}</>;
  }

  const splitKey = userId ? String(userId) : "anonymous";

  const sdkConfig: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey: process.env.NEXT_PUBLIC_SPLIT_API_KEY || "localhost",
      key: splitKey,
    },
    // Mock treatments, only honoured in "localhost" mode.
    features: process.env.NEXT_PUBLIC_SPLIT_API_KEY
      ? undefined
      : Object.fromEntries(
          Object.values(SPLIT_FLAGS).map((flagName) => [
            flagName,
            process.env[`NEXT_PUBLIC_SPLIT_MOCK_${flagName.toUpperCase()}`] ||
              "off",
          ]),
        ),
  };

  return (
    // key: re-initialize the SDK when the user changes.
    <SplitFactoryProvider config={sdkConfig} key={splitKey}>
      {children}
    </SplitFactoryProvider>
  );
}

export default SplitProvider;
